<template>
  <div class="projects" data-page="projects">
    <div class="projects__preload">
      <img
        v-for="(project, i) in $store.state.projects[$store.state.lang]"
        :key="`${project.title}-${i}`"
        :src="`${project.cover}`"
        :alt="`${project.title}`"
      >
    </div>
    <transition name="fade-image">
      <img
        class="projects__image"
        :src="currentImg"
        :alt="currentAlt"
        :key="currentIndex"
        v-if="currentIndex !== -1"
      >
    </transition>

    <section class="projects__container">
      <div
        class="projects__item"
        v-for="(project, i) in $store.state.projects[$store.state.lang]"
        :key="i"
        @mouseenter="hoverIn(project, i)"
        @mouseleave="currentIndex = -1"
        data-enter="voice"
      >
        <router-link
          :class="[
            'project__name',
            currentIndex !== i && currentIndex !== -1 ? 'project__name--disable' : '',
          ]"
          :to="`/${$store.state.lang}/${$store.state.lang === 'en' ? 'projects' : 'progetti'}${project.link}`"
        >
          {{ project.title }}
        </router-link>
        <svg
          :style="{
            opacity: currentIndex !== -1 ? 0.3 : 1,
          }"
          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 27"
          v-if="i !== $store.state.projects[$store.state.lang].length - 1"
        >
          <circle cx="13.5" cy="13.5" r="4" fill="#231f20"/>
        </svg>
      </div>
    </section>
  </div>
</template>

<script>
import HorizontalScroll from 'horizontal-scroll';
import TimelineLite from 'gsap/TimelineLite';
import { Power4 } from 'gsap/EasePack';
import { isMobile, qsa } from '@/scripts/utils';


export default {
  name: 'projects',

  data() {
    return {
      hs: undefined,

      currentImg: undefined,
      currentAlt: undefined,
      currentIndex: -1,

      isMobile,
    };
  },

  mounted() {
    this.initializeScroll();
    this.enterAnimation();
  },

  methods: {
    initializeScroll() {
      if (!isMobile()) {
        this.hs = new HorizontalScroll({
          blocks: document.getElementsByClassName('projects__item'),
          container: document.getElementsByClassName('projects__container'),
          spring: 0.1,
          isAnimated: true,
          skewLimit: 10,
        });
      }
    },

    enterAnimation() {
      const elsToTween = qsa('[data-enter="voice"]', this.$el);
      elsToTween.forEach(el => el.removeAttribute('data-enter'));

      const tl = new TimelineLite();
      tl.staggerFromTo(elsToTween, 1, {
        x: 100,
        skewX: -30,
        autoAlpha: 0,
        transformOrigin: 'center',
      }, {
        x: 0,
        skewX: 0,
        autoAlpha: 1,
        ease: Power4.easeOut,
        clearProps: 'all',
        delay: 1,
      }, 0.15);
    },

    hoverIn(obj, i) {
      if (!isMobile()) {
        this.currentImg = obj.cover;
        this.currentAlt = obj.title;
        this.currentIndex = i;
      }
    },
  },

  beforeDestroy() {
    if (this.hs !== undefined) this.hs.destroy();
  },
};
</script>

<style lang="scss" scoped>
@import "~sass-mq/mq";
@import '../style/colors';
@import '../style/variables';
@import '../style/functions';
@import '../style/mixins';


.projects {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 120px 20px 0;

  box-sizing: border-box;


  overflow: scroll;
  -webkit-overflow-scrolling: touch;

  @include mq($and: $is-not-touch-device) {
    display: flex;
    align-items: center;

    padding: 60px 0;

    overflow: hidden;
  }

  &__preload,
  &__preload img {
    @include visually-hidden;
  }

  &__item {
    position: relative;
    display: block;
    margin-bottom: 50px;

    @include mq($and: $is-not-touch-device) {
      display: inline-block;

      &:first-child {
        padding-left: 65px;
      }

      &:last-child {
        padding-right: 80px;
      }
    }

    & svg {
      display: none;
      width: 30px;
      margin: 0 50px 0 35px;

      transition: opacity 0.4s ease-out;

      @include mq($and: $is-not-touch-device) {
        display: inline-block;
      }
    }

    & a {
      font-size: font-size(xxxl);
      -webkit-text-stroke: 1.2px $color-text;
      letter-spacing: 1px;
      color: transparent;

      transition: color 0.4s ease-out;
    }

    &:hover a {
      color: $color-text;
    }

    &:hover .item__image {
      clip-path: inset(0 0 0 0);
      opacity: 1;
    }

    .project__name {
      transition: opacity 0.4s ease-out;
    }

    .project__name--disable {
      opacity: 0.3;
    }
  }

  &__image {
    position: fixed;
    top: 50%;
    left: 50%;
    width: auto;
    height: 80vh;

    transform: translate3d(-50%, -50%, 0);
    opacity: 1;
    clip-path: inset(0 0 0 0);

    z-index: -1;

    &.fade-image-enter-active {
      transition: all 0.8s ease-in-out 0.1s;
    }

    &.fade-image-leave-active {
      transition: all 0.8s ease-in-out;
    }

    &.fade-image-leave-to {
      clip-path: inset(0 0 100% 0);
      transform: translate3d(-50%, -120%, 0);
      opacity: 0;
    }

    &.fade-image-enter {
      clip-path: inset(100% 0 0 0);
      transform: translate3d(-50%, 20%, 0);
      opacity: 0;
    }
  }
}
</style>
